import React, { useEffect, useMemo, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { motion, AnimatePresence } from "framer-motion";
import {
  LogIn, LogOut, User, Cloud, X, ChevronLeft, ChevronRight, Menu, LifeBuoy,
  Home as HomeIcon, Building2, Store,
} from "lucide-react";

// ─────────────────────────────────────────────────────────────
// PATH NOTE
// This file assumes it lives at: src/SUPERAPP/HOME/AtlasHome.tsx
// All relative imports below are written from that location.
// If you drop this file somewhere else, adjust the "../" depth
// on every import (categories + subpages + redux) accordingly.
// ─────────────────────────────────────────────────────────────
import type { AppDispatch, RootState } from "../../redux/store";
import appLogo from "../../assets/app/logo.png";

// ═══════════════════════════════════════════════════════════
// CATEGORY DATA — single source of truth, nothing hardcoded here
// ═══════════════════════════════════════════════════════════
import {
  appCategories,
  getAppSubcategories,
  renderAppCategoryVisual,
  type CategoryItem,
} from "../../CATEGORIES/AppCategories";

// ═══════════════════════════════════════════════════════════
// REAL SUBPAGES — wired directly, no placeholders
// ═══════════════════════════════════════════════════════════
import VacationRentalsHome from "../BOOKING/VACATIONRENTALS/VacationRentals";
import TicketsHome from "../BOOKING/TICKETS/TicketsHome";
import AppointmentHome from "../BOOKING/APPOINTMENT/AppointmentHome";


/* ── SHOPPING — every Shopping sub-page lives together under
      src/SUPERAPP/SHOPPING/, imported from one consistent relative
      path ("../SHOPPING/X") instead of the mixed "../../SUPERAPP/..."
      paths some of these used to have. ── */
import AutomotiveHome from "../SHOPPING/AutomotiveHome";
import BabyHome from "../SHOPPING/BabyHome";
import BagsHome from "../SHOPPING/BagsHome";
import BeautyHome from "../SHOPPING/BeautyHome";
import BooksHome from "../SHOPPING/BooksHome";

import FarmHome from "../SHOPPING/FarmHome";
import FashionHome from "../SHOPPING/FashionHome";
import FurnitureHome from "../SHOPPING/FurnitureHome";
import KitchenHome from "../SHOPPING/KitchenHome";
import ProductsHome from "../SHOPPING/ProductsHome";

import { logout } from "../../redux/authSlice";
import CleaningHome from "../../SUPERAPP/BOOKING/CLEANING/DASHBOARD/CleaningHome";
import GroceriesHome from "../../SUPERAPP/SHOPPING/GROCERIES/GroceriesHome";
import ElectronicsHome from "../../SUPERAPP/SHOPPING/ELECTRONICS/ElectronicsHome";

// ── Chat — same self-contained panel used in FoodDashboard.tsx. Reused
//    here as a centered modal (desktop) / anchored dropdown (mobile). ──
import IMessagePreview from "../../MESSAGES/IMessagePreview";

// ── Orders — was previously unwired; id 4 ("Orders") used to accidentally
//    collide with a leftover GAMES_CATEGORY_ID=4 check below and render
//    GamesHome instead. That collision is removed; these are now real. ──
import ClientProductOrders from "../CLIENTDASHBOARD/ClientProductOrders";
import ShortBookingsDashboard from "../BOOKING/VACATIONRENTALS/ShortBookings/ShortBookingsDashboard";
import SmartFooter from "../../GLOBAL/SmartFooter";
import BookRide from "SUPERAPP/BOOKING/RIDE/BookRide";
import RiderHome from "SUPERAPP/BOOKING/RIDE/RiderHome";




/* ═══════════════════════════════════════════════════════════
   ROUTES
═══════════════════════════════════════════════════════════ */
const ROUTES = {
  home: "/",
  profile: (userId?: string) => `/profile/${userId ?? ""}`,
  support: (userId?: string) => `/CustomerSupport/${userId ?? ""}`,
  signIn: "/AuthSwitch",
};

/* ═══════════════════════════════════════════════════════════
   SUBCATEGORY ID → REAL COMPONENT
   NOTE: ids 207–213 are placeholders for the newly-added Shopping
   pages (Beauty/Bags/Books/Furniture/Kitchen/Automotive/Farm) —
   swap them for whatever ids AppCategories.tsx actually assigns
   those subcategories, same as the existing "adjust ids" notes
   elsewhere in this codebase.
═══════════════════════════════════════════════════════════ */
const SCREEN_COMPONENTS: Record<number, React.FC> = {
  201: GroceriesHome,        // Groceries
  202: ProductsHome,         // Products
  //203: FastHome,             // Fast Food
  204: FashionHome,          // Fashion & Beauty
  205: ElectronicsHome,      // Electronics
  206: BabyHome,             // Baby & Maternity
  207: BeautyHome,           // Beauty
  208: BagsHome,             // Bags
  209: BooksHome,            // Books
  210: FurnitureHome,        // Furniture
  211: KitchenHome,          // Kitchen
  212: AutomotiveHome,       // Automotive
  213: FarmHome,             // Farm

  301: VacationRentalsHome,  // Vacation Rentals
   //302: HotelsHome,        // Hotels — component not built yet
  /// 303: VacantRentalsHome, // Vacant Rentals — component not built yet
   304: CleaningHome,      // Cleaners — component not built yet
  // 305: MoversHome,        // Movers — component not built yet
  306: TicketsHome,          // Tickets
  307: AppointmentHome,      // Appointment
  308: RiderHome,             // Ride

  // Orders category (id 4) subcategories — now wired for real.
  401: ClientProductOrders,       // Orders
  402: ShortBookingsDashboard,    // Trips
};

const HOME_CATEGORY_ID = 1;
const SHOPPING_CATEGORY_ID = 2; // NOTE: adjust if Shopping's real id in AppCategories.tsx differs
const ORDERS_CATEGORY_ID = 4;   // "Orders" top-level category
const CHAT_CATEGORY_ID = 5;     // "Chat" top-level category

/** Default landing screen for signed-in users — Products, inside Shopping,
 *  instead of the empty hero/showcase view. */
const DEFAULT_ACTIVE_SUBCATEGORY_ID = 202; // ProductsHome

/* ═══════════════════════════════════════════════════════════
   HEARTLAS DESIGN TOKENS — pulled from the swan logo
   (deep crimson red, warm gold, near-black outline)
═══════════════════════════════════════════════════════════ */
const HEART = {
  bg: "#FBF6EE",
  panelBg: "#FFFFFF",
  headerBg: "#FFFFFF",

  dark: "#1B0E06",
  black: "#1B0E06",
  textMuted: "#7A6653",
  textFaint: "#A6947F",

  red: "#C8202E",
  redDark: "#9E1521",
  redSoft: "#FBE7E9",

  gold: "#F2A93E",
  goldDark: "#D4900F",
  goldSoft: "#FCEFD4",

  border: "#EEDFC4",
  divider: "#F3E6CE",
  danger: "#C8202E",
};

const SIDEBAR_W = 260;
const SIDEBAR_W_COLLAPSED = 68;

const GLOBAL_CSS = `
  *,*::before,*::after{box-sizing:border-box;}
  .fbh-root{font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",Helvetica,Arial,sans-serif;background:${HEART.bg};min-height:100vh;color:${HEART.dark};}
  .fbh-root ::-webkit-scrollbar{width:8px;height:8px;}
  .fbh-root ::-webkit-scrollbar-thumb{background:${HEART.border};border-radius:8px;}
  .fbh-root button{font-family:inherit;}
  .fbh-root :focus-visible{outline:2px solid ${HEART.gold};outline-offset:2px;}

  @keyframes aeSpin{0%{background-position:0% 50%}50%{background-position:100% 50%}100%{background-position:0% 50%}}
  @keyframes cardPop{from{opacity:0;transform:translateY(10px) scale(.97);}to{opacity:1;transform:translateY(0) scale(1);}}
  @keyframes wiggle{0%,100%{transform:rotate(-2deg);}50%{transform:rotate(2deg);}}

  .fbh-navitem{
    display:flex;align-items:center;gap:12px;width:100%;background:transparent;border:none;
    cursor:pointer;padding:10px 12px;border-radius:12px;color:${HEART.dark};font-weight:600;
    font-size:15px;text-align:left;transition:background .15s,color .15s,box-shadow .15s;
  }
  .fbh-navitem:hover{background:${HEART.goldSoft};}
  .fbh-navitem.active{
    background:linear-gradient(135deg, ${HEART.red}, ${HEART.redDark});
    color:#fff;
    box-shadow:0 6px 16px rgba(200,32,46,.35);
  }
  .fbh-navitem.collapsed{justify-content:center;padding:10px;}

  .fbh-navicon{
    width:36px;height:36px;border-radius:50%;background:${HEART.goldSoft};display:flex;
    align-items:center;justify-content:center;flex-shrink:0;color:${HEART.red};transition:background .15s,color .15s;
  }
  .fbh-navitem.active .fbh-navicon{background:#fff;color:${HEART.red};}

  .fbh-navlabel{white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}

  .fbh-subitem{
    display:flex;align-items:center;gap:10px;width:100%;background:transparent;border:none;
    cursor:pointer;padding:8px 10px 8px 30px;border-radius:10px;color:${HEART.textMuted};
    font-weight:600;font-size:13.5px;text-align:left;transition:background .15s,color .15s;
  }
  .fbh-subitem:hover{background:${HEART.goldSoft};color:${HEART.dark};}
  .fbh-subitem.active{background:${HEART.gold};color:${HEART.dark};font-weight:800;}

  .fbh-logout{
    display:flex;align-items:center;gap:12px;width:100%;background:transparent;border:none;
    cursor:pointer;padding:10px 12px;border-radius:12px;color:${HEART.danger};font-weight:700;
    font-size:14.5px;text-align:left;transition:background .15s;
  }
  .fbh-logout:hover{background:${HEART.redSoft};}
  .fbh-logout.collapsed{justify-content:center;padding:10px;}

  .fbh-support{
    display:flex;align-items:center;gap:12px;width:100%;background:transparent;border:none;
    cursor:pointer;padding:10px 12px;border-radius:12px;color:${HEART.dark};font-weight:700;
    font-size:14.5px;text-align:left;transition:background .15s;
  }
  .fbh-support:hover{background:${HEART.goldSoft};}
  .fbh-support.collapsed{justify-content:center;padding:10px;}

  .fbh-collapse-toggle{
    display:flex;align-items:center;justify-content:center;width:32px;height:32px;border-radius:10px;
    background:${HEART.goldSoft};border:none;color:${HEART.redDark};cursor:pointer;flex-shrink:0;
    transition:background .15s,color .15s;
  }
  .fbh-collapse-toggle:hover{background:${HEART.gold};color:#fff;}

  .fbh-tabbar{
    position:fixed;left:0;right:0;bottom:0;display:flex;background:${HEART.panelBg};
    border-top:1px solid ${HEART.border};z-index:40;padding-bottom:env(safe-area-inset-bottom);
    box-shadow:0 -4px 16px rgba(27,14,6,0.06);
  }
  .fbh-tab{
    flex:1;display:flex;flex-direction:column;align-items:center;justify-content:center;
    gap:2px;padding:8px 4px 10px;background:transparent;border:none;cursor:pointer;color:${HEART.textFaint};
    transition:color .15s;width:100%;
  }
  .fbh-tab.active{color:${HEART.red};}
  .fbh-tab span{font-size:11px;font-weight:700;}

  .fbh-sheet-card{
    display:flex;flex-direction:column;align-items:center;justify-content:center;gap:8px;
    aspect-ratio:1/1;background:${HEART.panelBg};border:1.5px solid ${HEART.border};border-radius:16px;
    cursor:pointer;padding:10px;transition:transform .1s,box-shadow .1s,border-color .1s;
  }
  .fbh-sheet-card:active{transform:scale(0.96);}
  .fbh-sheet-card:hover{box-shadow:0 6px 18px rgba(27,14,6,0.10);border-color:${HEART.gold};}

  .fbh-backbtn{
    display:inline-flex;align-items:center;gap:6px;
    background:linear-gradient(135deg, ${HEART.red}, ${HEART.redDark});color:#fff;
    border:none;border-radius:999px;padding:12px 26px;font-weight:800;font-size:14px;cursor:pointer;
    transition:filter .15s;box-shadow:0 8px 20px rgba(200,32,46,.32);
  }
  .fbh-backbtn:hover{filter:brightness(1.06);}

  .fbh-fab{
    position:fixed;right:20px;bottom:20px;width:54px;height:54px;border-radius:50%;
    background:linear-gradient(135deg, ${HEART.red}, ${HEART.redDark});color:#fff;border:none;
    display:flex;align-items:center;justify-content:center;overflow:hidden;
    box-shadow:0 6px 18px rgba(200,32,46,0.45);cursor:pointer;z-index:50;transition:filter .15s;
  }
  .fbh-fab:hover{filter:brightness(1.08);}

  .fbh-showcase-card{
    position:relative;display:flex;flex-direction:column;align-items:center;justify-content:center;
    gap:10px;background:#fff;border:2px solid ${HEART.gold};border-radius:18px;
    padding:22px 14px;cursor:pointer;transition:transform .15s,box-shadow .15s,border-color .15s;
    animation:cardPop .35s ease both;
  }
  .fbh-showcase-card:hover{
    transform:translateY(-4px);
    box-shadow:0 12px 26px rgba(200,32,46,0.16);
    border-color:${HEART.red};
  }
  .fbh-showcase-iconwrap{
    width:56px;height:56px;border-radius:50%;
    background:linear-gradient(135deg, ${HEART.red}, ${HEART.redDark});
    color:#fff;display:flex;align-items:center;justify-content:center;
    box-shadow:0 6px 14px rgba(200,32,46,0.3);
  }
  .fbh-showcase-label{font-size:13.5px;font-weight:800;color:${HEART.dark};text-align:center;}

  /* ── logged-out header / drawer ── */
  .hb-drawer-item{
    display:flex;align-items:center;gap:12px;width:100%;background:transparent;border:none;
    cursor:pointer;padding:14px 10px;border-radius:14px;color:${HEART.dark};
    font-weight:900;font-size:16.5px;text-align:left;letter-spacing:-.2px;
    text-shadow:0 1px 0 rgba(255,255,255,.7);transition:background .15s,transform .1s;
  }
  .hb-drawer-item:hover{background:${HEART.goldSoft};transform:translateX(3px);}

  .hb-login-btn{
    margin-top:auto;display:flex;align-items:center;justify-content:center;gap:8px;
    width:100%;padding:15px;border-radius:8px;border:none;cursor:pointer;
    background:${HEART.red};color:#fff;font-weight:900;font-size:15.5px;letter-spacing:.02em;
    box-shadow:0 8px 20px rgba(200,32,46,.35);transition:filter .15s,transform .1s;
  }
  .hb-login-btn:hover{filter:brightness(1.08);transform:translateY(-1px);}

  .hb-logo-wiggle{animation:wiggle 2.6s ease-in-out infinite;transform-origin:center;}

  @media (prefers-reduced-motion: reduce){ * { animation:none !important; transition:none !important; } }
`;

/* ═══════════════════════════════════════════════════════════
   CATEGORY SHOWCASE — reusable card grid
   Used both on the signed-out screen (below the logo) and on
   the Home view for signed-in users (below the greeting hero).
═══════════════════════════════════════════════════════════ */
const CategoryShowcase: React.FC<{
  items: CategoryItem[];
  onSelect: (item: CategoryItem) => void;
  title?: string;
}> = ({ items, onSelect, title }) => {
  if (!items.length) return null;
  return (
    <div style={{ width: "100%" }}>
      {title && <div style={S.showcaseTitle}>{title}</div>}
      <div style={S.showcaseGrid}>
        {items.map((item, i) => (
          <button
            key={item.id}
            className="fbh-showcase-card"
            style={{ animationDelay: `${i * 45}ms` }}
            onClick={() => onSelect(item)}
          >
            <span className="fbh-showcase-iconwrap">
              {renderAppCategoryVisual(item, 26)}
            </span>
            <span className="fbh-showcase-label">{item.name}</span>
          </button>
        ))}
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════
   MAIN COMPONENT
═══════════════════════════════════════════════════════════ */
const AtlasHome: React.FC = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 900);
  // Defaults to Products (inside Shopping) instead of the empty hero view.
  const [activeSubId, setActiveSubId] = useState<number | null>(DEFAULT_ACTIVE_SUBCATEGORY_ID);
  // Shopping starts expanded to match the default active subcategory above.
  const [openDesktopSection, setOpenDesktopSection] = useState<number | null>(SHOPPING_CATEGORY_ID);
  const [mobileSheetParentId, setMobileSheetParentId] = useState<number | null>(null);
  const [sidebarOpen, setSidebarOpen] = useState(true); // desktop sidebar collapse state
  const [menuOpen, setMenuOpen] = useState(false); // header profile/menu slide-in panel (logged-in)

  // Chat — centered modal on desktop, anchored dropdown on mobile,
  // same pattern as FoodDashboard's IMessagePreview panel.
  const [chatOpen, setChatOpen] = useState(false);
  const chatRef = useRef<HTMLDivElement>(null);

  // Logged-out hamburger drawer.
  const [hamburgerOpen, setHamburgerOpen] = useState(false);

  useEffect(() => {
    const fn = () => setIsMobile(window.innerWidth <= 900);
    fn();
    window.addEventListener("resize", fn);
    return () => window.removeEventListener("resize", fn);
  }, []);

  // Outside-click-to-close for the mobile anchored chat dropdown only —
  // the desktop modal already has its own full-screen backdrop click handler.
  useEffect(() => {
    if (!chatOpen || !isMobile) return;
    const fn = (e: MouseEvent) => {
      if (chatRef.current && !chatRef.current.contains(e.target as Node)) setChatOpen(false);
    };
    document.addEventListener("mousedown", fn);
    return () => document.removeEventListener("mousedown", fn);
  }, [chatOpen, isMobile]);

  const dispatch = useDispatch<AppDispatch>();

  const user = useSelector((s: RootState) => s.auth.user);
  const authLoaded = useSelector((s: RootState) => s.auth.authLoaded);
  const userId = user?.userId;
  const firstName = user?.firstName || "";
  const avatarSrc = user?.profilePicture || appLogo; // ✅ always falls back to our logo

  const navigate = (path: string) => { window.location.href = path; }; // swap for useNavigate() if this file sits under a Router

  const handleLogout = async () => {
    try {
      await dispatch(logout()).unwrap();
    } finally {
      navigate("/AuthSwitch");
    }
  };

  /** Return to the hero/showcase landing view — used by the footer's
   *  Home button and by tapping "Home" wherever it still appears. */
  const goHome = () => {
    setActiveSubId(null);
    setOpenDesktopSection(null);
    setMobileSheetParentId(null);
    setChatOpen(false);
  };

  const handleCategoryTap = (category: CategoryItem) => {
    if (category.id === HOME_CATEGORY_ID) {
      goHome();
      return;
    }

    // Chat — auth-gated. Opens the message-preview modal (same pattern as
    // FoodDashboard) instead of expanding a submenu. Logged-out users get
    // bounced to sign-in.
    if (category.id === CHAT_CATEGORY_ID) {
      if (!user) { navigate(ROUTES.signIn); return; }
      setChatOpen(true);
      return;
    }

    // Orders — same auth gate as Chat, then falls through to the normal
    // submenu flow below so its two subcategories (Orders/Trips, now wired
    // to ClientProductOrders / ShortBookingsDashboard) behave like every
    // other category's submenu.
    if (category.id === ORDERS_CATEGORY_ID && !user) {
      navigate(ROUTES.signIn);
      return;
    }

    if (isMobile) {
      setMobileSheetParentId((prev) => (prev === category.id ? null : category.id));
      return;
    }
    // desktop: if the sidebar is collapsed, expand it first so the submenu has room
    if (!sidebarOpen) {
      setSidebarOpen(true);
      setOpenDesktopSection(category.id);
      return;
    }
    setOpenDesktopSection((prev) => (prev === category.id ? null : category.id));
  };

  const handleSubcategoryTap = (sub: CategoryItem, parentCategoryId: number) => {
    setActiveSubId(sub.id);
    setChatOpen(false);
    // Shopping stays expanded after picking a subcategory so every
    // shopping category remains visible/reachable in the sidebar —
    // every other section still collapses as before.
    setOpenDesktopSection(parentCategoryId === SHOPPING_CATEGORY_ID ? SHOPPING_CATEGORY_ID : null);
    setMobileSheetParentId(null);
  };

  const ActiveComponent: React.FC | null =
    activeSubId != null ? SCREEN_COMPONENTS[activeSubId] ?? null : null;

  // top-level categories minus "Home" itself, for the sidebar list and
  // mobile tab bar (Home now lives in the footer instead).
  const nonHomeCategories = useMemo(
    () => appCategories.filter((c) => c.id !== HOME_CATEGORY_ID),
    []
  );
  const homeCategoryItem = useMemo(
    () => appCategories.find((c) => c.id === HOME_CATEGORY_ID) ?? null,
    []
  );

  // Showcase cards (Home hero grid + signed-out grid) drop Orders and Chat —
  // those are now auth-gated actions, not generic browse categories.
  const showcaseCategories = useMemo(
    () => nonHomeCategories.filter(
      (c) => c.id !== ORDERS_CATEGORY_ID && c.id !== CHAT_CATEGORY_ID
    ),
    [nonHomeCategories]
  );

  /** Whether a given top-level category should render as "active" —
   *  Chat is active while its modal is open; everything else is active
   *  when one of its subcategories is the current screen. */
  const isCategoryActiveFor = (category: CategoryItem) => {
    if (category.id === HOME_CATEGORY_ID) return activeSubId == null && !chatOpen;
    if (category.id === CHAT_CATEGORY_ID) return chatOpen;
    return getAppSubcategories(category.id).some((s) => s.id === activeSubId);
  };

  // ── still checking auth ──
  if (!authLoaded) {
    return (
      <div className="fbh-root" style={S.page}>
        <style>{GLOBAL_CSS}</style>
        <div style={S.loaderBox}>
          <Cloud size={34} color={HEART.red} />
        </div>
      </div>
    );
  }

  // ── not signed in — cute header + drawer + hero + footer ──
  if (!user) {
    return (
      <div className="fbh-root" style={S.page}>
        <style>{GLOBAL_CSS}</style>

        {/* ── top bar: hamburger + logo + List Your Property CTA ── */}
        <header style={S.loggedOutHeader}>
          <button
            style={S.hamburgerBtn}
            onClick={() => setHamburgerOpen(true)}
            aria-label="Open menu"
          >
            <Menu size={22} color={HEART.dark} />
          </button>

          <div style={S.loggedOutBrand}>
            <img src={appLogo} alt="Heartlas logo" style={S.loggedOutLogo} className="hb-logo-wiggle" />
            <span style={S.loggedOutBrandName}>Heartlas</span>
          </div>

          <button style={S.listPropertyBtn} onClick={() => navigate("/hosting")}>
            List Your Property
          </button>
        </header>

        {/* ── slide-in side menu ── */}
        <AnimatePresence>
          {hamburgerOpen && (
            <>
              <motion.div
                key="hb-scrim"
                initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                style={S.hbScrim}
                onClick={() => setHamburgerOpen(false)}
              />
              <motion.nav
                key="hb-drawer"
                aria-label="Main menu"
                initial={{ x: "-100%" }} animate={{ x: 0 }} exit={{ x: "-100%" }}
                transition={{ type: "tween", duration: 0.22 }}
                style={S.hbDrawer}
              >
                <div style={S.hbDrawerHeader}>
                  <img src={appLogo} alt="Heartlas logo" style={S.hbDrawerLogo} />
                  <span style={S.hbDrawerBrand}>Heartlas <span aria-hidden="true">😊</span></span>
                  <button
                    style={S.hbCloseBtn}
                    onClick={() => setHamburgerOpen(false)}
                    aria-label="Close menu"
                  >
                    <X size={18} />
                  </button>
                </div>

                <button className="hb-drawer-item" onClick={() => navigate("/hosting")}>
                  <Building2 size={20} color={HEART.redDark} /> List Your Property
                </button>
                <button className="hb-drawer-item" onClick={() => navigate("/Selling")}>
                  <Store size={20} color={HEART.redDark} /> Start Selling
                </button>
                <button className="hb-drawer-item" onClick={() => navigate("/support")}>
                  <LifeBuoy size={20} color={HEART.redDark} /> Customer Service
                </button>

                <button className="hb-login-btn" onClick={() => navigate(ROUTES.signIn)}>
                  <LogIn size={17} /> Login
                </button>
              </motion.nav>
            </>
          )}
        </AnimatePresence>

        <main style={S.signInWrap}>
          <img
            src={appLogo}
            alt="Heartlas — shop, book, and order everything in one app"
            style={S.signInLogo}
            onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = "none"; }}
          />
          <h1 style={S.signInTitle}>Welcome to Heartlas</h1>
          <p style={S.signInBlurb}>Sign in to see your home, your shopping, your bookings — all in one place.</p>
          <button className="fbh-backbtn" style={S.signInBtn} onClick={() => navigate(ROUTES.signIn)}>
            <LogIn size={16} /> Sign in
          </button>

          <div style={S.signedOutShowcaseWrap}>
            <CategoryShowcase
              title="Explore what's inside"
              items={showcaseCategories}
              onSelect={() => navigate(ROUTES.signIn)}
            />
          </div>
        </main>

        <SmartFooter />
      </div>
    );
  }

  const currentSidebarW = sidebarOpen ? SIDEBAR_W : SIDEBAR_W_COLLAPSED;

  // ── hub / app shell (sidebar + tabbar always present; content area swaps) ──
  return (
    <div className="fbh-root" style={S.page}>
      <style>{GLOBAL_CSS}</style>

      {/* header — ONLY visible on the Home hero view, matches ShortAccomodation header exactly */}
      {!ActiveComponent && (
        <div style={S.header}>
          <div style={S.brandRow}>
            <div style={S.logoRingOuter}>
              <div style={S.logoRingBorder} />
              <div style={S.logoRingInner}>
                <img
                  src={appLogo}
                  alt="Heartlas"
                  style={S.logoImg}
                  onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = "none"; }}
                />
              </div>
            </div>
            {!isMobile && <span style={S.greeting}>Hi, {firstName || "there"}</span>}
          </div>

          {/* profile / menu button — opens the gold/red slide-in sidebar */}
          <button
            style={S.profileMenuBtn}
            onClick={() => setMenuOpen(true)}
            onMouseDown={(e) => {
              e.currentTarget.style.transform = "translateY(1px)";
              e.currentTarget.style.boxShadow = "0 1px 3px rgba(27,14,6,.18)";
            }}
            onMouseUp={(e) => {
              e.currentTarget.style.transform = "translateY(0)";
              e.currentTarget.style.boxShadow = "0 3px 10px rgba(27,14,6,.12)";
            }}
          >
            <img
              src={avatarSrc}
              alt={firstName || "Profile"}
              onError={(e) => {
                (e.currentTarget as HTMLImageElement).onerror = null;
                (e.currentTarget as HTMLImageElement).src = appLogo;
              }}
              style={S.profileAvatar}
            />
            <Menu size={18} color={HEART.dark} />
          </button>
        </div>
      )}

      <div style={{ display: "flex", height: ActiveComponent ? "100vh" : "calc(100vh - 61px)", overflow: "hidden" }}>

        {/* ── desktop sidebar (collapsible, ALWAYS visible incl. while a subpage renders) ── */}
        {!isMobile && (
          <div style={{ ...S.sidebar, width: currentSidebarW, top: ActiveComponent ? 0 : 61, height: ActiveComponent ? "100vh" : "calc(100vh - 61px)" }}>
            <div style={S.sidebarToggleRow}>
              {sidebarOpen && <span style={S.sidebarToggleLabel}>Menu</span>}
              <button
                className="fbh-collapse-toggle"
                onClick={() => setSidebarOpen((prev) => !prev)}
                aria-label={sidebarOpen ? "Collapse sidebar" : "Expand sidebar"}
                title={sidebarOpen ? "Collapse sidebar" : "Expand sidebar"}
              >
                {sidebarOpen ? <ChevronLeft size={16} /> : <ChevronRight size={16} />}
              </button>
            </div>

            <div style={{ flex: 1, overflowY: "auto" }}>
              {nonHomeCategories.map((category) => {
                const subs = getAppSubcategories(category.id);
                const isChat = category.id === CHAT_CATEGORY_ID;
                const isCategoryActive = isCategoryActiveFor(category);
                return (
                  <div key={category.id} style={{ marginBottom: 4 }}>
                    <button
                      className={`fbh-navitem${isCategoryActive ? " active" : ""}${!sidebarOpen ? " collapsed" : ""}`}
                      onClick={() => handleCategoryTap(category)}
                      title={category.name}
                    >
                      <span className="fbh-navicon">{renderAppCategoryVisual(category, 18)}</span>
                      {sidebarOpen && <span className="fbh-navlabel">{category.name}</span>}
                    </button>
                    {sidebarOpen && !isChat && openDesktopSection === category.id && (
                      <div style={{ display: "flex", flexDirection: "column", gap: 2, marginTop: 4, marginBottom: 8 }}>
                        {subs.map((sub) => (
                          <button
                            key={sub.id}
                            className={`fbh-subitem${activeSubId === sub.id ? " active" : ""}`}
                            onClick={() => handleSubcategoryTap(sub, category.id)}
                          >
                            {renderAppCategoryVisual(sub, 14)}
                            <span>{sub.name}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            <div style={S.sidebarFooter}>
              {/* Home — moved above Support so it reads as a fixed utility
                  action rather than just another scrollable category. */}
              <button
                className={`fbh-navitem${activeSubId == null && !chatOpen ? " active" : ""}${!sidebarOpen ? " collapsed" : ""}`}
                onClick={goHome}
                title="Home"
              >
                <span className="fbh-navicon">
                  {homeCategoryItem ? renderAppCategoryVisual(homeCategoryItem, 18) : <HomeIcon size={18} />}
                </span>
                {sidebarOpen && <span className="fbh-navlabel">Home</span>}
              </button>
              <button
                className={`fbh-support${!sidebarOpen ? " collapsed" : ""}`}
                onClick={() => navigate(ROUTES.support(userId))}
                title="Support"
              >
                <LifeBuoy size={18} color={HEART.redDark} />
                {sidebarOpen && <span>Support</span>}
              </button>
              <button
                className={`fbh-logout${!sidebarOpen ? " collapsed" : ""}`}
                onClick={handleLogout}
                title="Log out"
              >
                <LogOut size={18} />
                {sidebarOpen && <span>Log out</span>}
              </button>
            </div>
          </div>
        )}

        {/* ── main content: Home hero (+ category showcase) by default, or the imported component directly ── */}
        <div
          style={{
            ...S.content,
            maxWidth: ActiveComponent ? "none" : S.content.maxWidth,
            padding: ActiveComponent ? 0 : S.content.padding,
            marginLeft: isMobile ? 0 : 0,
            marginBottom: isMobile ? 76 : 0,
          }}
        >
          {ActiveComponent ? (
            ActiveComponent === VacationRentalsHome ? (
              <VacationRentalsHome embedded />
            ) : (
              <ActiveComponent />
            )
          ) : (
            <>
              <div style={S.hero}>
                <div style={S.heroTitle}>Where to today, {firstName || "friend"}?</div>
                <p style={S.heroBlurb}>Pick a category below (or from the sidebar) to get started.</p>
              </div>
              <CategoryShowcase items={showcaseCategories} onSelect={handleCategoryTap} />
            </>
          )}
        </div>
      </div>

      {/* ── desktop chat modal — centered, escapes any parent overflow:hidden ── */}
      {!isMobile && (
        <AnimatePresence>
          {chatOpen && (
            <>
              <motion.div
                initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                onClick={() => setChatOpen(false)}
                style={S.chatBackdrop}
              />
              <motion.div
                initial={{ opacity: 0, scale: 0.94, y: 16 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.94, y: 16 }}
                transition={{ type: "spring", stiffness: 340, damping: 30 }}
                style={S.chatModal}
              >
                <div style={S.chatModalHeader}>
                  <span style={S.chatModalTitle}>Chat</span>
                  <button onClick={() => setChatOpen(false)} style={S.chatModalClose}>
                    <X size={18} />
                  </button>
                </div>
                <IMessagePreview />
              </motion.div>
            </>
          )}
        </AnimatePresence>
      )}

      {/* ── mobile bottom tab bar (stays visible even while a subpage renders — acts as the mobile "sidebar") ── */}
      {isMobile && (
        <div className="fbh-tabbar">
          {appCategories.map((category) => {
            const isChat = category.id === CHAT_CATEGORY_ID;
            const isCategoryActive =
              mobileSheetParentId === category.id || isCategoryActiveFor(category);
            return (
              <div key={category.id} style={{ position: "relative", flex: 1, display: "flex" }}>
                <button
                  className={`fbh-tab${isCategoryActive ? " active" : ""}`}
                  onClick={() => handleCategoryTap(category)}
                >
                  {renderAppCategoryVisual(category, 22)}
                  <span>{category.name}</span>
                </button>

                {/* mobile chat — anchored dropdown near its tab, same pattern as FoodDashboard */}
                {isChat && (
                  <AnimatePresence>
                    {chatOpen && (
                      <>
                        <div style={S.chatMobileScrim} onClick={() => setChatOpen(false)} />
                        <motion.div
                          ref={chatRef}
                          initial={{ opacity: 0, y: 8, scale: 0.97 }}
                          animate={{ opacity: 1, y: 0, scale: 1 }}
                          exit={{ opacity: 0, y: 8, scale: 0.97 }}
                          transition={{ duration: 0.15 }}
                          style={S.chatMobilePanel}
                        >
                          <IMessagePreview />
                        </motion.div>
                      </>
                    )}
                  </AnimatePresence>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* ── mobile bottom sheet: square subcategory cards ── */}
      <AnimatePresence>
        {isMobile && mobileSheetParentId != null && (
          <>
            <motion.div
              key="scrim"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              style={S.sheetScrim}
              onClick={() => setMobileSheetParentId(null)}
            />
            <motion.div
              key="sheet"
              initial={{ y: "100%" }}
              animate={{ y: 0 }}
              exit={{ y: "100%" }}
              transition={{ type: "tween", duration: 0.22 }}
              style={S.sheet}
            >
              <div style={S.sheetHandleRow}>
                <span style={S.sheetTitle}>
                  {appCategories.find((c) => c.id === mobileSheetParentId)?.name}
                </span>
                <button style={S.sheetCloseBtn} onClick={() => setMobileSheetParentId(null)}>
                  <X size={18} color={HEART.textMuted} />
                </button>
              </div>
              <div style={S.sheetGrid}>
                {getAppSubcategories(mobileSheetParentId).map((sub) => (
                  <div
                    key={sub.id}
                    className="fbh-sheet-card"
                    onClick={() => handleSubcategoryTap(sub, mobileSheetParentId as number)}
                  >
                    {renderAppCategoryVisual(sub, 26)}
                    <span style={S.sheetCardLabel}>{sub.name}</span>
                  </div>
                ))}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* ── header profile/menu slide-in panel — gold/red, matches ShortAccomodation's side menu ── */}
      {menuOpen && (
        <div style={S.sidebarOverlay} onClick={() => setMenuOpen(false)}>
          <div style={S.menuPanel} onClick={(e) => e.stopPropagation()}>
            <div style={S.menuPanelHeader}>
              <img
                src={avatarSrc}
                alt={firstName || "Profile"}
                onError={(e) => {
                  (e.currentTarget as HTMLImageElement).onerror = null;
                  (e.currentTarget as HTMLImageElement).src = appLogo;
                }}
                style={S.menuPanelAvatar}
              />
              <div>
                <div style={S.menuPanelName}>{firstName || "Welcome"}</div>
                <div style={S.menuPanelSub}>Manage your account</div>
              </div>
            </div>

            <button style={S.closeBtn} onClick={() => setMenuOpen(false)}>
              <X size={18} />
            </button>

            <div style={S.menuPanelContent}>
              <button onClick={() => navigate(ROUTES.profile(userId))} style={S.menuPanelItem}>
                Profile
              </button>
              <button onClick={() => navigate(ROUTES.support(userId))} style={S.menuPanelItem}>
                Support
              </button>
              <button onClick={handleLogout} style={S.menuPanelItemDanger}>
                Logout
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── floating profile button, bottom right — defaults to our logo ── */}
      <button className="fbh-fab" onClick={() => navigate(ROUTES.profile(userId))} aria-label="Profile">
        <img
          src={avatarSrc}
          alt=""
          onError={(e) => { (e.currentTarget as HTMLImageElement).src = appLogo; }}
          style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: "50%" }}
        />
      </button>
    </div>
  );
};

export default AtlasHome;

/* ═══════════════════════════════════════════════════════════
   STYLES
═══════════════════════════════════════════════════════════ */
const S: Record<string, React.CSSProperties> = {
  page: { minHeight: "100vh", width: "100%", background: HEART.bg, color: HEART.dark, overflowX: "hidden" },
  header: {
    display: "flex", alignItems: "center", justifyContent: "space-between",
    padding: "10px 20px", borderBottom: `1px solid ${HEART.border}`, background: HEART.headerBg,
    position: "sticky", top: 0, zIndex: 30,
  },
  brandRow: { display: "flex", alignItems: "center", gap: 12 },

  // ── spinning gold-border logo ──
  logoRingOuter: { position: "relative", display: "inline-flex", borderRadius: 12 },
  logoRingBorder: {
    position: "absolute", inset: -2, borderRadius: 14,
    background: `linear-gradient(90deg, ${HEART.dark}, ${HEART.gold}, ${HEART.red}, ${HEART.gold}, ${HEART.dark})`,
    backgroundSize: "300% 100%",
    animation: "aeSpin 3s linear infinite",
    zIndex: 0,
  },
  logoRingInner: {
    position: "relative", zIndex: 1, background: "#fff", borderRadius: 12,
    padding: 3, display: "inline-flex",
  },
  logoImg: { height: 34, width: 34, borderRadius: 10, objectFit: "cover", display: "block" },

  greeting: { fontSize: 14, fontWeight: 700, color: HEART.dark },

  // ── profile/menu pill button ──
  profileMenuBtn: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    height: 44,
    padding: "0 14px",
    minWidth: 76,
    borderRadius: 999,
    border: `1px solid ${HEART.border}`,
    background: "#fff",
    boxShadow: "0 3px 10px rgba(27,14,6,.12)",
    cursor: "pointer",
    transition: "box-shadow .2s, transform .15s",
  },
  profileAvatar: { width: 26, height: 26, borderRadius: "50%", objectFit: "cover", border: `1.5px solid ${HEART.gold}` },

  sidebar: {
    position: "sticky", alignSelf: "flex-start",
    padding: "16px 10px", display: "flex", flexDirection: "column", borderRight: `1px solid ${HEART.border}`,
    flexShrink: 0, transition: "width .18s ease", overflow: "hidden",
    background: HEART.bg, zIndex: 20,
  },
  sidebarToggleRow: { display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0 2px 12px" },
  sidebarToggleLabel: { fontSize: 12, fontWeight: 800, color: HEART.textMuted, textTransform: "uppercase", letterSpacing: ".06em" },
  sidebarFooter: { borderTop: `1px solid ${HEART.divider}`, paddingTop: 10, marginTop: 10, display: "flex", flexDirection: "column", gap: 2 },

  content: { flex: 1, maxWidth: 980, padding: "24px 20px 60px", width: "100%", minWidth: 0, height: "100%", overflowY: "auto", overflowX: "hidden" },

  hero: {
    background: `linear-gradient(135deg, ${HEART.panelBg}, ${HEART.goldSoft})`,
    border: `1px solid ${HEART.border}`, borderRadius: 16,
    padding: "24px 22px", marginBottom: 24,
    boxShadow: "0 6px 20px rgba(27,14,6,0.05)",
  },
  heroTitle: { fontSize: 22, fontWeight: 900, color: HEART.dark, margin: 0 },
  heroBlurb: { fontSize: 13.5, color: HEART.textMuted, marginTop: 6 },

  loaderBox: { display: "flex", alignItems: "center", justifyContent: "center", height: "100vh" },

  // ── logged-out top bar ──
  loggedOutHeader: {
    display: "flex", alignItems: "center", justifyContent: "space-between",
    gap: 12, padding: "14px 18px", position: "sticky", top: 0, zIndex: 50,
    background: "rgba(255,255,255,0.92)", backdropFilter: "blur(8px)",
    borderBottom: `1px solid ${HEART.border}`,
  },
  hamburgerBtn: {
    width: 42, height: 42, borderRadius: 14, border: `1px solid ${HEART.border}`,
    background: "#fff", display: "flex", alignItems: "center", justifyContent: "center",
    cursor: "pointer", boxShadow: "0 3px 10px rgba(27,14,6,.08)", flexShrink: 0,
  },
  loggedOutBrand: { display: "flex", alignItems: "center", gap: 8, minWidth: 0 },
  loggedOutLogo: { width: 30, height: 30, borderRadius: "50%", objectFit: "cover", border: `1.5px solid ${HEART.gold}`, display: "block" },
  loggedOutBrandName: { fontWeight: 900, fontSize: 15, color: HEART.dark, letterSpacing: -0.2, whiteSpace: "nowrap" },
  listPropertyBtn: {
    padding: "10px 16px", borderRadius: 999, border: "none",
    background: `linear-gradient(135deg, ${HEART.gold}, ${HEART.red})`, color: "#fff",
    fontWeight: 800, fontSize: 12.5, cursor: "pointer", whiteSpace: "nowrap",
    boxShadow: "0 6px 16px rgba(200,32,46,.28)", flexShrink: 0,
  },

  // ── logged-out slide-in drawer ──
  hbScrim: { position: "fixed", inset: 0, background: "rgba(27,14,6,0.5)", zIndex: 6000 },
  hbDrawer: {
    position: "fixed", left: 0, top: 0, bottom: 0, width: "min(300px, 85vw)",
    background: `linear-gradient(165deg, ${HEART.goldSoft} 0%, #fff 45%, #fff 100%)`,
    zIndex: 6001, boxShadow: "0 0 30px rgba(27,14,6,0.35)",
    padding: "22px 18px", display: "flex", flexDirection: "column", gap: 6,
  },
  hbDrawerHeader: { display: "flex", alignItems: "center", gap: 10, marginBottom: 20, position: "relative" },
  hbDrawerLogo: { width: 38, height: 38, borderRadius: "50%", objectFit: "cover", border: `2px solid ${HEART.gold}` },
  hbDrawerBrand: { fontWeight: 900, fontSize: 17, color: HEART.dark },
  hbCloseBtn: {
    position: "absolute", right: 0, top: -4, width: 32, height: 32, borderRadius: "50%",
    border: `1px solid ${HEART.border}`, background: "#fff", cursor: "pointer",
    display: "flex", alignItems: "center", justifyContent: "center",
  },

  signInWrap: {
    display: "flex", flexDirection: "column", alignItems: "center",
    padding: "48px 24px 60px", textAlign: "center", gap: 4,
  },
  signInLogo: {
    width: 170,
    height: 170,
    objectFit: "contain",
    marginBottom: 6,
    filter: "drop-shadow(0 14px 26px rgba(200,32,46,0.22))",
  },
  signInTitle: { fontSize: 24, fontWeight: 900, color: HEART.dark, marginTop: 6 },
  signInBlurb: { fontSize: 13.5, color: HEART.textMuted, maxWidth: 340, lineHeight: 1.5, marginBottom: 18 },
  signInBtn: { padding: "12px 26px", fontSize: 14 },

  signedOutShowcaseWrap: { marginTop: 48, width: "100%", maxWidth: 760 },

  showcaseTitle: {
    fontSize: 13, fontWeight: 800, color: HEART.textMuted,
    textTransform: "uppercase", letterSpacing: ".06em",
    marginBottom: 14, textAlign: "left",
  },
  showcaseGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(120px, 1fr))",
    gap: 14,
    width: "100%",
  },

  sheetScrim: { position: "fixed", inset: 0, background: "rgba(27,14,6,0.45)", zIndex: 45 },
  sheet: {
    position: "fixed", left: 0, right: 0, bottom: 0, background: HEART.panelBg, zIndex: 46,
    borderTopLeftRadius: 20, borderTopRightRadius: 20, padding: "10px 16px calc(20px + env(safe-area-inset-bottom))",
    maxHeight: "70vh", overflowY: "auto", boxShadow: "0 -10px 30px rgba(27,14,6,.18)",
  },
  sheetHandleRow: { display: "flex", alignItems: "center", justifyContent: "space-between", padding: "6px 2px 14px" },
  sheetTitle: { fontSize: 16, fontWeight: 900, color: HEART.dark },
  sheetCloseBtn: { background: HEART.goldSoft, border: "none", borderRadius: "50%", width: 28, height: 28, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" },
  sheetGrid: { display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12 },
  sheetCardLabel: { fontSize: 12, fontWeight: 700, color: HEART.dark, textAlign: "center" },

  // ── gold/red slide-in menu panel (logged in) ──
  sidebarOverlay: {
    position: "fixed",
    inset: 0,
    backgroundColor: "rgba(27,14,6,0.55)",
    display: "flex",
    justifyContent: "flex-end",
    zIndex: 6000,
  },
  menuPanel: {
    width: "100%",
    maxWidth: 360,
    height: "100%",
    background: `linear-gradient(160deg, ${HEART.goldSoft} 0%, #fff 42%, #fff 100%)`,
    boxShadow: "0 0 30px rgba(27,14,6,0.35)",
    padding: 24,
    position: "relative",
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
  },
  menuPanelHeader: { display: "flex", alignItems: "center", gap: 12, marginTop: 46, marginBottom: 4 },
  menuPanelAvatar: { width: 52, height: 52, borderRadius: "50%", objectFit: "cover", border: `2px solid ${HEART.gold}` },
  menuPanelName: { fontWeight: 900, fontSize: 16, color: HEART.dark },
  menuPanelSub: { fontSize: 12.5, color: HEART.textMuted, marginTop: 2 },
  closeBtn: {
    position: "absolute",
    top: 24,
    right: 24,
    borderRadius: "50%",
    width: 36,
    height: 36,
    border: `1px solid ${HEART.border}`,
    background: "#fff",
    cursor: "pointer",
  },
  menuPanelContent: {
    marginTop: 28,
    display: "flex",
    flexDirection: "column",
    gap: 12,
    width: "100%",
  },
  menuPanelItem: {
    padding: "13px 16px",
    borderRadius: 14,
    border: `1px solid ${HEART.border}`,
    background: "#fff",
    color: HEART.dark,
    fontWeight: 700,
    textAlign: "left",
    cursor: "pointer",
    transition: "transform .15s ease, box-shadow .15s ease, border-color .15s",
  },
  menuPanelItemDanger: {
    padding: "13px 16px",
    borderRadius: 14,
    border: `1px solid ${HEART.redSoft}`,
    background: HEART.redSoft,
    color: HEART.redDark,
    fontWeight: 800,
    textAlign: "left",
    cursor: "pointer",
    transition: "transform .15s ease, box-shadow .15s ease",
  },

  // ── chat modal (desktop) / anchored panel (mobile) ──
  chatBackdrop: { position: "fixed", inset: 0, background: "rgba(27,14,6,.45)", zIndex: 1000, backdropFilter: "blur(4px)" },
  chatModal: {
    position: "fixed", top: "50%", left: "50%", transform: "translate(-50%,-50%)",
    zIndex: 1001, width: "min(480px, calc(100vw - 48px))",
    maxHeight: "min(640px, calc(100vh - 80px))", overflowY: "auto",
    background: "#fff", borderRadius: 20,
    boxShadow: "0 30px 80px rgba(27,14,6,.3)", border: `1.5px solid ${HEART.border}`,
  },
  chatModalHeader: {
    display: "flex", alignItems: "center", justifyContent: "space-between",
    padding: "16px 20px", borderBottom: `1px solid ${HEART.border}`,
  },
  chatModalTitle: { fontSize: 15, fontWeight: 800, color: HEART.dark },
  chatModalClose: { background: "none", border: "none", cursor: "pointer", color: HEART.textMuted },
  chatMobileScrim: { position: "fixed", inset: 0, zIndex: 998 },
  chatMobilePanel: {
    position: "fixed", right: 12, bottom: 88, width: "min(360px, calc(100vw - 24px))",
    maxHeight: "60vh", overflowY: "auto", background: "#fff",
    border: `1.5px solid ${HEART.border}`, borderRadius: 16,
    boxShadow: "0 20px 50px rgba(27,14,6,.18)", zIndex: 999,
  },
};